import React, { useState, useMemo } from "react";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import DataTable from "react-data-table-component";

import IndicatorRow from "AppSrc/project/indicators/equations";
import { CreateIndicatorForm } from "AppSrc/project/indicators/form";
import { ErrorBoundary } from "storybook-dashboard/errors/handleError";
import { Modal, useModal } from "storybook-dashboard/components/modal";
import CustomDropdown from "./dropdown";
import Im from "immutable";

import { formEventToData } from "AppSrc/utils";

const getName = (indicator) => {
  return indicator.getIn(["resultBaseMetric", "name"]) || indicator.get("name") || "";
};

// Column definitions
const getIndicatorColumns = (basemetrics) => [
  {
    name: "ID",
    selector: (row) => row.get("uid").substring(0, 8),
    sortable: true,
    width: "100px",
  },
  {
    name: "Result Base Metric ID",
    selector: (row) => row.getIn(["resultBaseMetric", "uid"])?.substring(0, 8) || "",
    sortable: true,
    width: "180px",
  },
  {
    name: "Name",
    selector: (row) => getName(row),
    sortable: true,
    grow: 3,
  },
  {
    name: "Category",
    selector: (row) => row.getIn(["resultBaseMetric", "category"]) || "",
    sortable: true,
    width: "150px",
  },
  {
    name: "Actions",
    cell: (row) => <ActionsCell row={row} basemetrics={basemetrics} />,
    width: "100px",
    right: true,
  },
];

// Expandable row component
const ExpandedIndicatorRow = ({ data: indicator, basemetrics }) => {
  return (
    <div style={{ padding: "20px", backgroundColor: "#f9f9f9" }}>
      <ErrorBoundary>
        <div className="row mt-3">
          <div className="col-sm-4">
            <b>{"Name"}</b>
          </div>
          <div className="col-sm-2">
            <b>{"Sustainability Issue"}</b>
          </div>
          <div className="col-sm-1">
            <b>{"Factor"}</b>
          </div>
          <div className="col-sm-3">
            <b>{"Numerators & Denominators"}</b>
          </div>
          <div className="col-sm-1">
            <b>Target</b> | <b>Warn</b>
          </div>
          <div className="col-sm-1">
            <b className="float-right">{"Admin"}</b>
          </div>
        </div>
        <IndicatorRow baseMetrics={basemetrics} indicator={indicator} />
      </ErrorBoundary>
    </div>
  );
};

const mergeIndicators = ({ e, indicatorId, onSuccess }) => {
  const { from_indicator_id } = formEventToData(e);

  doFetch("/api/tenant/admin/dispatch/", "POST", {
    type: "MERGE_INDICATORS",
    payload: {
      from_indicator_id,
      to_indicator_id: indicatorId,
    },
  })
    .then((response) => response.json())
    .then((data) => onSuccess?.(data));
};

function MergeIndicatorModal({ indicatorId }) {
  const { hideModal } = useModal();
  const { trigger } = useApi("/api/tenant/admin/indicator/");
  const [data, setData] = useState({ from_indicator_id: "" });

  const onSuccess = (data) => {
    alert(JSON.stringify(data, null, 2));

    trigger();
    hideModal();
  };

  return (
    <Modal title={`Merge indicator to ${indicatorId.substring(0, 8)}`}>
      <form onSubmit={(e) => mergeIndicators({ e, indicatorId, onSuccess })}>
        <div className="form-group">
          <label htmlFor="from_indicator_id">Indicator ID to merge into this</label>
          <input
            type="text"
            className="form-control"
            id="from_indicator_id"
            name="from_indicator_id"
            placeholder="Enter MetricID to merge from"
            value={data.from_indicator_id}
            onChange={(e) => setData({ ...data, from_indicator_id: e.target.value })}
          />
          <small className="form-text text-muted">
            Note: The ID entered above will be deleted and redirect to this indicator
          </small>
        </div>
        <button type="submit" className="btn btn-primary">
          Merge
        </button>
      </form>
    </Modal>
  );
}

function EditIndicatorModal({ indicator, basemetrics }) {
  const { trigger } = useApi("/api/tenant/admin/indicator/");
  const { hideModal } = useModal();
  const indicatorId = indicator.get("uid");

  const onSuccess = () => {
    trigger();
    hideModal();
  };

  return (
    <Modal title={`Edit indicator: ${indicatorId.substring(0, 8)}`}>
      <CreateIndicatorForm
        noProject={true}
        indicator={indicator}
        baseMetrics={basemetrics}
        toggleShowHandler={(e) => hideModal()}
        fetchUrl={`/api/tenant/admin/indicator/${indicatorId}/`}
        dispatchType={"PUT"}
        onSuccess={onSuccess}
      />
    </Modal>
  );
}

// Actions cell component
function ActionsCell({ row, basemetrics }) {
  const { setModal } = useModal();

  const dropDownItems = [
    {
      name: "Edit",
      onClick: () => {
        setModal(<EditIndicatorModal indicator={row} basemetrics={basemetrics} />, true);
      },
    },
    {
      name: "Merge",
      onClick: () => {
        setModal(<MergeIndicatorModal indicatorId={row.get("uid")} />);
      },
    },
  ];

  return <CustomDropdown items={dropDownItems} />;
}

export default function BaseIndicatorAdmin() {
  const { data: indicators } = useApi("/api/tenant/admin/indicator/");
  const { data: basemetrics } = useApi("/api/tenant/admin/basemetric/");
  const [searchTerm, setSearchTerm] = useState("");

  const baseMetricsById = useMemo(
    () => basemetrics?.reduce((a, i) => a.set(i.get("uid"), i), Im.Map()) || Im.Map(),
    [basemetrics]
  );

  // Column definitions
  const columns = useMemo(() => getIndicatorColumns(baseMetricsById), [baseMetricsById]);

  // Sort indicators by result base metric name
  const sortedIndicators = useMemo(() => {
    return (indicators || Im.List()).sortBy((i) => getName(i)?.trim() || "");
  }, [indicators]);

  // Filter indicators based on search term
  const filteredIndicators = useMemo(() => {
    if (!searchTerm.trim()) {
      return sortedIndicators;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return sortedIndicators.filter((indicator) => {
      const id = indicator.get("uid") || "";
      const name = getName(indicator);
      const category = indicator.getIn(["resultBaseMetric", "category"]) || "";

      return (
        id.toLowerCase().includes(lowerSearchTerm) ||
        name.toLowerCase().includes(lowerSearchTerm) ||
        category.toLowerCase().includes(lowerSearchTerm)
      );
    });
  }, [sortedIndicators, searchTerm]);

  if (searchTerm.trim()) {
    console.log("Filtered Indicators", filteredIndicators.toJS());
  }

  return (
    <>
      <h3>Indicators in Tenancy</h3>
      <p>This gives a list of all Indicators within this tenancy</p>

      <div style={{ marginBottom: "16px" }}>
        <input
          type="text"
          className="form-control"
          placeholder="Search by ID, Name, or Category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: "400px" }}
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredIndicators.toArray()}
        pagination
        paginationPerPage={20}
        paginationRowsPerPageOptions={[10, 20, 50, 100]}
        highlightOnHover={true}
        striped={true}
        defaultSortFieldId={3} // Sort by Name column by default
        expandableRows
        expandableRowsComponent={(props) => <ExpandedIndicatorRow {...props} basemetrics={baseMetricsById} />}
      />
    </>
  );
}
